import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  CalendarToday as CalendarTodayIcon,
  History as HistoryIcon,
  Cancel as CancelIcon,
  AddCircle as AddCircleIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  ArrowForward as ArrowForwardIcon,
  Folder as FolderIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import api from '../../config/axiosConfig';
import useConemVersion from '../../hooks/useConemVersion';
import AvisoEntregasPendientes from '../perfil-riesgo/AvisoEntregasPendientes';

// Formato compacto de moneda para las tarjetas de KPI -- antes todo se dividía entre 1,000,000
// fijo ("$X.XM"), así que con montos típicos de esta operación (decenas de miles por cotización)
// siempre se veía "$0.0M", indistinguible de cero. Ahora escala según el monto: miles ("K") o
// millones ("M") según corresponda, y montos menores a $1,000 se muestran completos.
const formatCompactCurrency = (value) => {
  const amount = Number(value) || 0;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
};

const Home = () => {
  const navigate = useNavigate();
  const appVersion = useConemVersion();
  // Una sola descarga de /quotations compartida por todas las secciones de Home (antes cada una
  // la pedía por su cuenta: 4 veces al abrir y 1 cada minuto con el refresco de notificaciones).
  // Se reutiliza 5 minutos; también si varias la piden al mismo tiempo (comparten la petición).
  const quotationsCache = useRef({ promise: null, at: 0 });
  const getQuotations = () => {
    const cache = quotationsCache.current;
    if (cache.promise && Date.now() - cache.at < 5 * 60 * 1000) return cache.promise;
    cache.at = Date.now();
    cache.promise = api.get('/quotations').catch(error => {
      cache.promise = null;
      throw error;
    });
    return cache.promise;
  };

  // Recordatorios derivados de las cotizaciones (no son filas de la tabla notifications); se
  // agregan encima de las notificaciones reales. Los descartados no vuelven en esta sesión.
  const dismissedReminders = useRef(new Set());
  // El refresco de 60 s conserva la función del primer render (cuando aún no había rol), así que
  // el rol se lee de un ref siempre actualizado
  const currentRoleRef = useRef(null);
  const buildQuotationReminders = (quotations) => {
    const reminders = [];
    if (!['ADMIN', 'ADMINISTRADOR', 'VENTAS'].includes(currentRoleRef.current)) return reminders;
    const unsentDrafts = quotations.filter(q => q.status === 'DRAFT');
    // Nuevas versiones de cotizaciones ya canceladas: al cliente se le avisó que llegaría una nueva
    const awaitedReplacements = unsentDrafts.filter(q => q.parentQuotationId
      && quotations.some(p => p.id === q.parentQuotationId && p.status === 'CANCELLED'));
    if (awaitedReplacements.length > 0) {
      reminders.push({
        id: 'awaited-replacements',
        type: 'error',
        message: `${awaitedReplacements.length} nueva${awaitedReplacements.length === 1 ? '' : 's'} versi${awaitedReplacements.length === 1 ? 'ón' : 'ones'} sin enviar: el cliente espera la propuesta que reemplaza a la cancelada (${awaitedReplacements.map(q => q.invoiceFolio).join(', ')})`,
        time: 'Ahora',
        actionUrl: `/cotizaciones/new-quotation?edit=${awaitedReplacements[0].id}`,
        actionLabel: 'Abrir'
      });
    }
    const otherDrafts = unsentDrafts.length - awaitedReplacements.length;
    if (otherDrafts > 0) {
      reminders.push({
        id: 'unsent-drafts',
        type: 'warning',
        message: `${otherDrafts} cotizaci${otherDrafts === 1 ? 'ón' : 'ones'} en borrador sin enviar al cliente`,
        time: 'Ahora',
        actionUrl: '/estadisticas/cotizaciones',
        actionState: { cardFilter: 'DRAFT' },
        actionLabel: 'Ver borradores'
      });
    }
    // Correcciones en sitio de Operaciones pendientes de revisar si impactan el costo
    const costReviews = quotations.filter(q => q.pendingCostReviews > 0);
    if (costReviews.length > 0) {
      reminders.push({
        id: 'pending-cost-reviews',
        type: 'warning',
        message: `💲 ${costReviews.length} cotizaci${costReviews.length === 1 ? 'ón' : 'ones'} con correcciones en sitio por revisar costo (${costReviews.map(q => q.invoiceFolio).join(', ')})`,
        time: 'Ahora',
        actionUrl: '/estadisticas/cotizaciones',
        actionLabel: 'Revisar costo'
      });
    }
    return reminders.filter(r => !dismissedReminders.current.has(r.id));
  };

  // Admins: "Mis notificaciones" (campana, solo propias)y "Supervisión" (todas, agrupadas por
  // evento con sus destinatarios; solo lectura, no marca nada por nadie)
  const [notifTab, setNotifTab] = useState('mine');
  const [supervisionFeed, setSupervisionFeed] = useState([]);
  const [loadingSupervision, setLoadingSupervision] = useState(false);
  const fetchSupervisionFeed = async () => {
    setLoadingSupervision(true);
    try {
      const response = await api.get('/notifications/supervision?days=30');
      setSupervisionFeed(response.data || []);
    } catch (error) {
      console.error('Error cargando supervisión de notificaciones:', error);
      setSupervisionFeed([]);
    } finally {
      setLoadingSupervision(false);
    }
  };

  const [currentUser, setCurrentUser] = useState({
    name: 'Cargando...',
    role: 'Usuario',
    avatar: null
  });
  // Cuando se conoce el rol, recalcular notificaciones (los recordatorios dependen de él)
  useEffect(() => {
    const role = ['Usuario', 'Cargando...'].includes(currentUser.role) ? null : currentUser.role;
    const changed = currentRoleRef.current !== role;
    currentRoleRef.current = role;
    if (changed && role) fetchNotifications();
  }, [currentUser.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const [quotationDetailsOpen, setQuotationDetailsOpen] = useState(false);
  const [revenueDetailsOpen, setRevenueDetailsOpen] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [guestQuotations, setGuestQuotations] = useState([]);
  const [guestWorkOrders, setGuestWorkOrders] = useState([]);
  const [loadingGuestData, setLoadingGuestData] = useState(false);
  const [conversionStats, setConversionStats] = useState({
    enviadas: 0,
    aceptadas: 0,
    conversionRate: 0,
    loading: true
  });
  const [quotationData, setQuotationData] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    recentQuotations: [],
    loading: true
  });
  
  const [userStats, setUserStats] = useState({
    active: 0,
    inactive: 0,
    total: 0,
    loading: true
  });

  // Estado para distribución de servicios dinámica
  const [distributionView, setDistributionView] = useState('risk_level'); // risk_level, client_type, service_type, attention_group
  const [serviceDistribution, setServiceDistribution] = useState([]);
  const [loadingDistribution, setLoadingDistribution] = useState(true);

  // Estado para tendencias de ingresos
  const [revenueData, setRevenueData] = useState({
    monthlyData: [],
    totalReal: 0,
    totalProjected: 0,
    currentMonthReal: 0,
    currentMonthProjected: 0
  });
  const [loadingRevenue, setLoadingRevenue] = useState(true);

  // Dropbox health-check
  const [dropboxHealth, setDropboxHealth] = useState(null);

  // Mock data for dashboard (después de inicializar estados)
  const kpiData = {
    quotations: { total: 156, pending: 23, approved: 98, rejected: 35 },
    users: { active: userStats.active, today: 12, thisWeek: 38 },
    revenue: {
      projected: revenueData.currentMonthProjected,
      thisMonth: revenueData.currentMonthReal
    },
    services: { total: 89, active: 67, maintenance: 22 }
  };

  const [weeklyActivity, setWeeklyActivity] = useState([]);
  const [loadingWeeklyActivity, setLoadingWeeklyActivity] = useState(true);

  // Fetch user info and recent activity from backend
  useEffect(() => {
    fetchCurrentUser();
    fetchRecentActivity();
    fetchConversionStats();
    fetchNotifications();
    fetchQuotationData();
    fetchRevenueTrends();
    fetchUserStats();
    fetchServiceDistribution();
    fetchWeeklyActivity();
  }, []);

  // La campanita del Navbar reconsulta el contador cada 60s (ver Navbar.js) -- esta card, en
  // cambio, solo cargaba una vez al montar, así que si el usuario se quedaba en Home podía
  // quedar desincronizada del badge (mostrar 0 mientras la campanita ya marcaba pendientes).
  // Mismo intervalo para que ambas coincidan siempre.
  useEffect(() => {
    // Igual que la campanita: sin consultar con la pestaña oculta, y al volver se actualiza
    const interval = setInterval(() => { if (!document.hidden) fetchNotifications(); }, 60000);
    const handleVisibility = () => { if (!document.hidden) fetchNotifications(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Refetch distribution when view changes
  useEffect(() => {
    fetchServiceDistribution();
  }, [distributionView]);

  const fetchDropboxHealth = async () => {
    try {
      const response = await api.get('/dropbox/health-check');
      if (response.data && !response.data.connected) {
        setDropboxHealth(response.data);
      }
    } catch (err) {
      console.error('Error en health-check de Dropbox:', err);
      setDropboxHealth({ configured: false, connected: false, issue: 'No se pudo verificar el estado de Dropbox.' });
    }
  };

  const fetchGuestData = async (companyId) => {
    setLoadingGuestData(true);
    // Mismo endpoint y filtro que perfil-riesgo/historial (Historial.jsx: fetchQuotations),
    // para que ambas pantallas siempre muestren exactamente los mismos datos.
    const [quotationsResult, workOrdersResult] = await Promise.allSettled([
      getQuotations(),
      api.get(`/work-order-details/by-company/${companyId}`)
    ]);

    if (quotationsResult.status === 'fulfilled') {
      const data = quotationsResult.value.data;
      const allQuotations = Array.isArray(data) ? data : (data?.content || []);
      setGuestQuotations(allQuotations.filter(q => q.companyId === companyId));
    } else {
      console.error('Error cargando cotizaciones del invitado:', quotationsResult.reason);
      setGuestQuotations([]);
    }

    if (workOrdersResult.status === 'fulfilled') {
      const data = workOrdersResult.value.data;
      setGuestWorkOrders(Array.isArray(data) ? data : []);
    } else {
      console.error('Error cargando inspecciones del invitado:', workOrdersResult.reason);
      setGuestWorkOrders([]);
    }

    setLoadingGuestData(false);
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        const user = response.data;
        const fullName = `${user.firstName || ''} ${user.firstLastName || ''} ${user.secondLastName || ''}`.trim();
        
        // Obtener el rol del usuario - puede venir como objeto o string
        const userRole = user.role?.name || user.role || 'Usuario';
        
        console.log('Usuario actual:', { fullName, role: userRole, roleObject: user.role });
        
        const companyId = user.company?.id || null;
        setCurrentUser({
          name: fullName || 'Usuario',
          role: userRole,
          avatar: user.avatar || null,
          companyId
        });

        if (userRole === 'INVITADO' && companyId) {
          fetchGuestData(companyId);
        }

        // El banner de estado de Dropbox y su botón "Configurar" solo le sirven al
        // administrador (es quien puede entrar a Utilerías > Dropbox a resolverlo).
        if (userRole === 'ADMINISTRADOR') {
          fetchDropboxHealth();
        }
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      // Fallback to default user if API fails
      setCurrentUser({
        name: 'Usuario',
        role: 'Usuario',
        avatar: null
      });
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setLoadingActivity(true);
      const response = await api.get('/events/recent?limit=5');
      const events = response.data.map(event => ({
        id: event.id,
        action: event.description,
        // EventDTO no tiene createdBy, solo userName -- leer el campo equivocado hacía que
        // esto cayera siempre a 'Sistema' sin importar quién generó el evento.
        user: event.userName || 'Sistema',
        time: formatTimeAgo(event.createdAt),
        icon: getEventIcon(event.eventType),
        // Nuevos campos para soporte de múltiples roles
        allowedRoleIds: event.allowedRoleIds || null,
        allowedUserId: event.allowedUserId || null,
        eventType: event.eventType,
        entityType: event.entityType,
        metadata: event.metadata
      }));
      setRecentActivity(events);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // No mostrar datos de prueba, dejar vacío
      setRecentActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Cotizaciones creadas y usuarios distintos que generaron actividad (eventos) por día,
  // para la semana en curso (lunes a domingo). Antes era data fija en el componente.
  const fetchWeeklyActivity = async () => {
    try {
      setLoadingWeeklyActivity(true);

      const [quotationsRes, eventsRes] = await Promise.all([
        getQuotations(),
        api.get('/events')
      ]);
      const quotations = Array.isArray(quotationsRes.data) ? quotationsRes.data : [];
      const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];

      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Dom ... 6 = Sáb
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setHours(0, 0, 0, 0);
      monday.setDate(today.getDate() + diffToMonday);

      const dayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
      const isSameDay = (dateValue, target) => {
        if (!dateValue) return false;
        const d = new Date(dateValue);
        return d.getFullYear() === target.getFullYear() &&
          d.getMonth() === target.getMonth() &&
          d.getDate() === target.getDate();
      };

      const activity = dayLabels.map((label, i) => {
        const dayDate = new Date(monday);
        dayDate.setDate(monday.getDate() + i);

        const cotizaciones = quotations.filter(q => isSameDay(q.createdAt, dayDate)).length;
        const usuariosActivos = new Set(
          events
            .filter(e => isSameDay(e.createdAt, dayDate))
            .map(e => e.userId)
        ).size;

        return { day: label, cotizaciones, usuarios: usuariosActivos };
      });

      setWeeklyActivity(activity);
    } catch (error) {
      console.error('Error fetching weekly activity:', error);
      setWeeklyActivity([]);
    } finally {
      setLoadingWeeklyActivity(false);
    }
  };

  const fetchConversionStats = async () => {
    try {
      console.log('Obteniendo estadísticas de conversión...');
      
      // Obtener estadísticas de los últimos 30 días
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      console.log('Período:', startDate.toISOString(), 'a', endDate.toISOString());
      
      const response = await getQuotations();
      const quotations = response.data;
      
      console.log('Total cotizaciones obtenidas:', quotations.length);
      
      // Filtrar cotizaciones de los últimos 30 días
      const recentQuotations = quotations.filter(q => {
        if (!q.createdAt) return false;
        const createdAt = new Date(q.createdAt);
        return createdAt >= startDate && createdAt <= endDate;
      });
      
      console.log('Cotizaciones recientes (30 días):', recentQuotations.length);
      
      // TODAS las cotizaciones generadas (sin importar estado)
      const totalGeneradas = recentQuotations.length;
      
      // Servicios autorizados
      const aceptadas = recentQuotations.filter(q => 
        q.status === 'ACEPTADA' || q.status === 'APROBADA' || q.status === 'ACCEPTED'
      ).length;
      
      console.log('Estados encontrados:', [...new Set(recentQuotations.map(q => q.status))]);
      console.log('Generadas:', totalGeneradas, 'Aceptadas:', aceptadas);
      
      const conversionRate = totalGeneradas > 0 ? (aceptadas / totalGeneradas) * 100 : 0;
      
      setConversionStats({
        enviadas: totalGeneradas, // Renombramos para claridad: son las generadas
        aceptadas,
        conversionRate,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching conversion stats:', error);
      // Fallback con datos de ejemplo
      setConversionStats({
        enviadas: 45,
        aceptadas: 28,
        conversionRate: 62.2,
        loading: false
      });
    }
  };

  const fetchQuotationData = async () => {
    try {
      console.log('📊 Obteniendo datos de cotizaciones para el dashboard...');
      
      const response = await getQuotations();
      const quotations = Array.isArray(response.data) ? response.data : [];
      
      console.log('Total cotizaciones obtenidas:', quotations.length);
      
      // Calcular estadísticas
      const total = quotations.length;
      const pending = quotations.filter(q => 
        q.status === 'PENDING' || q.status === 'DRAFT' || q.status === 'PENDIENTE'
      ).length;
      const approved = quotations.filter(q => 
        q.status === 'ACCEPTED' || q.status === 'APROBADA' || q.status === 'ACEPTADA'
      ).length;
      const rejected = quotations.filter(q => 
        q.status === 'REJECTED' || q.status === 'RECHAZADA'
      ).length;
      
      // Obtener las 5 cotizaciones más recientes
      const sortedQuotations = [...quotations].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      const recentQuotations = sortedQuotations.slice(0, 5).map(q => ({
        id: q.folio || `COT-${q.id}`,
        client: q.clientName || q.businessName || 'Cliente sin nombre',
        amount: q.totalAmount || 0,
        status: getStatusLabel(q.status),
        date: q.createdAt ? new Date(q.createdAt).toLocaleDateString('es-MX') : 'Sin fecha',
        statusColor: getStatusColor(q.status)
      }));
      
      console.log('📈 Estadísticas calculadas:', { total, pending, approved, rejected });
      console.log('📋 Cotizaciones recientes:', recentQuotations);
      
      setQuotationData({
        total,
        pending,
        approved,
        rejected,
        recentQuotations,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching quotation data:', error);
      // Fallback con datos de ejemplo
      setQuotationData({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        recentQuotations: [],
        loading: false
      });
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'PENDING': 'Pendiente',
      'DRAFT': 'Borrador',
      'PENDIENTE': 'Pendiente',
      'ACCEPTED': 'Aprobada',
      'APROBADA': 'Aprobada',
      'ACEPTADA': 'Aprobada',
      'REJECTED': 'Rechazada',
      'RECHAZADA': 'Rechazada',
      'EXPIRED': 'Expirada',
      'EXPIRADA': 'Expirada'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'PENDING': 'warning',
      'DRAFT': 'default',
      'PENDIENTE': 'warning',
      'ACCEPTED': 'success',
      'APROBADA': 'success',
      'ACEPTADA': 'success',
      'REJECTED': 'error',
      'RECHAZADA': 'error',
      'EXPIRED': 'default',
      'EXPIRADA': 'default'
    };
    return colorMap[status] || 'default';
  };

  const fetchUserStats = async () => {
    try {
      console.log('📊 Obteniendo estadísticas de usuarios...');
      const response = await api.get('/users/role-statistics');
      
      if (response.data) {
        const stats = response.data;
        console.log('✅ Estadísticas de usuarios recibidas:', stats);
        
        setUserStats({
          active: stats.usuariosActivos || 0,
          inactive: stats.usuariosInactivos || 0,
          total: stats.totalUsuarios || 0,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Fallback con datos de ejemplo
      setUserStats({
        active: 0,
        inactive: 0,
        total: 0,
        loading: false
      });
    }
  };

  const fetchServiceDistribution = async () => {
    try {
      setLoadingDistribution(true);
      console.log('📊 Obteniendo distribución de servicios por:', distributionView);
      
      // Usar el nuevo endpoint de estadísticas por ENUMs
      const response = await api.get('/quotations/statistics/by-enums');
      const statistics = response.data || {};
      
      console.log('📊 Estadísticas recibidas del backend:', statistics);
      
      // Configuración de colores por tipo de vista
      const colorConfig = {
        risk_level: {
          'BAJO': '#4caf50',
          'MEDIO': '#ff9800',
          'ALTO': '#f44336'
        },
        client_type: {
          'NUEVO': '#2196f3',
          'RECURRENTE': '#9c27b0'
        },
        service_type: {
          'PROTECCION_CIVIL': '#8884d8',
          'CURSO': '#82ca9d',
          'OTRO': '#ffc658'
        },
        attention_group: {
          'VERACRUZ': '#e91e63',
          'CDMX': '#3f51b5'
        }
      };
      
      // Mapear distributionView a la clave correcta en statistics
      const viewKeyMap = {
        'risk_level': 'riskLevel',
        'client_type': 'clientType',
        'service_type': 'serviceType',
        'attention_group': 'attentionGroup'
      };
      
      const statsKey = viewKeyMap[distributionView];
      const viewData = statistics[statsKey];
      
      if (!viewData || !viewData.data) {
        console.warn('⚠️ No hay datos para:', distributionView);
        setServiceDistribution([]);
        return;
      }
      
      const colors = colorConfig[distributionView] || {};
      const total = viewData.total || 0;
      
      // Convertir datos del backend al formato del frontend
      const distributionArray = viewData.data.map(item => ({
        name: item.displayName || item.name,
        value: item.count,
        color: colors[item.name] || '#9e9e9e',
        percentage: total > 0 ? ((item.count / total) * 100).toFixed(1) : 0
      }));
      
      // Ordenar por valor descendente
      distributionArray.sort((a, b) => b.value - a.value);
      
      console.log('📈 Distribución calculada:', distributionArray);
      setServiceDistribution(distributionArray);
      
    } catch (error) {
      console.error('Error fetching service distribution:', error);
      // Fallback con datos de ejemplo
      setServiceDistribution([
        { name: 'Bajo', value: 35, color: '#4caf50', percentage: 35 },
        { name: 'Medio', value: 45, color: '#ff9800', percentage: 45 },
        { name: 'Alto', value: 20, color: '#f44336', percentage: 20 }
      ]);
    } finally {
      setLoadingDistribution(false);
    }
  };

  const fetchRevenueTrends = async () => {
    try {
      setLoadingRevenue(true);
      console.log('💰 Obteniendo tendencias de ingresos...');
      
      const response = await api.get('/quotations/statistics/revenue-trends');
      const data = response.data || {};
      
      console.log('💰 Tendencias de ingresos recibidas:', data);
      
      setRevenueData({
        monthlyData: data.monthlyData || [],
        totalReal: data.totalReal || 0,
        totalProjected: data.totalProjected || 0,
        currentMonthReal: data.currentMonthReal || 0,
        currentMonthProjected: data.currentMonthProjected || 0
      });
      
    } catch (error) {
      console.error('Error fetching revenue trends:', error);
      // Fallback con datos de ejemplo
      setRevenueData({
        monthlyData: [
          { month: 'Ene', ingresos: 1800000, proyectado: 2000000 },
          { month: 'Feb', ingresos: 2100000, proyectado: 2200000 },
          { month: 'Mar', ingresos: 1950000, proyectado: 2100000 },
          { month: 'Abr', ingresos: 2300000, proyectado: 2400000 },
          { month: 'May', ingresos: 2150000, proyectado: 2300000 },
          { month: 'Jun', ingresos: 2450000, proyectado: 2500000 }
        ],
        totalReal: 12750000,
        totalProjected: 13500000,
        currentMonthReal: 2450000,
        currentMonthProjected: 2500000
      });
    } finally {
      setLoadingRevenue(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      
      // Primero intentar obtener notificaciones reales del sistema
      try {
        const userResponse = await api.get('/users/me');
        const userId = userResponse.data?.id;
        console.log('🔍 Usuario actual para notificaciones:', { userId, userData: userResponse.data });
        if (userId) setCurrentUserId(userId);

        if (userId) {
          console.log('📡 Llamando a /notifications con userId:', userId);
          const notificationsResponse = await api.get(`/notifications?userId=${userId}`);
          const realNotifications = notificationsResponse.data || [];
          console.log('📬 Notificaciones recibidas del backend:', realNotifications);
          
          // Convertir notificaciones del backend al formato del frontend
          const formattedNotifications = realNotifications.map(notification => ({
            id: notification.id,
            type: getNotificationTypeFromBackend(notification.notificationType),
            message: notification.message,
            time: notification.timeAgo || 'Reciente',
            actionUrl: notification.actionUrl || null,
            actionLabel: notification.actionLabel
          }));
          
          console.log('✅ Notificaciones formateadas:', formattedNotifications);
          
          const reminders = buildQuotationReminders(
            await getQuotations().then(r => (Array.isArray(r.data) ? r.data : [])).catch(() => []));
          if (formattedNotifications.length > 0 || reminders.length > 0) {
            setNotifications([...reminders, ...formattedNotifications]);
            return;
          } else {
            console.log('⚠️ No hay notificaciones reales, usando fallback');
          }
        } else {
          console.log('❌ No se pudo obtener userId');
        }
      } catch (notificationError) {
        console.log('❌ Sistema de notificaciones no disponible, usando fallback:', notificationError.message);
        console.error('Error completo:', notificationError);
      }
      
      // Fallback: Generar notificaciones basadas en datos del sistema
      const [quotationsRes, workOrdersRes] = await Promise.all([
        getQuotations(),
        api.get('/work-orders').catch(() => ({ data: [] }))
      ]);

      const quotations = Array.isArray(quotationsRes.data) ? quotationsRes.data : [];
      const workOrdersData = workOrdersRes.data;
      
      // Manejar diferentes formatos de respuesta de work-orders
      let workOrders = [];
      if (Array.isArray(workOrdersData)) {
        workOrders = workOrdersData;
      } else if (workOrdersData && Array.isArray(workOrdersData.content)) {
        workOrders = workOrdersData.content; // Formato paginado
      } else if (workOrdersData && workOrdersData.data && Array.isArray(workOrdersData.data)) {
        workOrders = workOrdersData.data;
      }
      
      console.log('📊 Datos obtenidos:', { 
        quotations: quotations.length, 
        workOrders: workOrders.length,
        workOrdersRaw: workOrdersData 
      });
      
      const generatedNotifications = [];
      
      // Notificaciones para administradores
      if (currentUser.role === 'ADMIN' || currentUser.role === 'ADMINISTRADOR') {
        // Cotizaciones pendientes
        const pendingQuotations = quotations.filter(q => q.status === 'PENDING');
        if (pendingQuotations.length > 0) {
          generatedNotifications.push({
            id: 'pending-quotations',
            type: 'warning',
            message: `${pendingQuotations.length} cotizaciones enviadas esperando respuesta del cliente`,
            time: 'Ahora',
            action: () => navigate('/estadisticas'),
            actionLabel: 'Ver cotizaciones'
          });
        }

        // Órdenes de trabajo programadas para hoy
        // Fecha LOCAL, no UTC -- toISOString() adelanta el día después de ~18:00 en México
        // (UTC-6): con eso, tanto "programadas para hoy" como "vencidas" (abajo) comparaban
        // contra el día equivocado.
        const today = new Date().toLocaleDateString('en-CA');
        const todayOrders = Array.isArray(workOrders) ? workOrders.filter(wo =>
          wo.scheduledDate && wo.scheduledDate.startsWith(today)
        ) : [];
        if (todayOrders.length > 0) {
          generatedNotifications.push({
            id: 'today-orders',
            type: 'info',
            message: `${todayOrders.length} órdenes de trabajo programadas para hoy`,
            time: 'Ahora',
            action: () => navigate('/servicios/agenda'),
            actionLabel: 'Ver agenda'
          });
        }

        // Órdenes vencidas -- comparación por fecha (string), no por Date con hora: antes
        // `new Date(wo.scheduledDate) < new Date()` marcaba como vencida CUALQUIER orden
        // programada para hoy durante todo el día (medianoche UTC del día programado ya
        // había "pasado" para cualquier hora local en México).
        const overdueOrders = Array.isArray(workOrders) ? workOrders.filter(wo => {
          if (!wo.scheduledDate) return false;
          return wo.scheduledDate.substring(0, 10) < today && wo.status !== 'COMPLETED';
        }) : [];
        if (overdueOrders.length > 0) {
          generatedNotifications.push({
            id: 'overdue-orders',
            type: 'error',
            message: `${overdueOrders.length} órdenes de trabajo vencidas`,
            time: 'Ahora',
            action: () => navigate('/servicios/agenda'),
            actionLabel: 'Revisar órdenes'
          });
        }
      }
      
      // Notificaciones para usuarios regulares
      else {
        // Obtener el ID del usuario actual para filtrar órdenes asignadas
        const userResponse = await api.get('/users/me');
        const currentUserId = userResponse.data?.id;
        
        if (currentUserId) {
          // Órdenes asignadas al usuario
          const userOrders = Array.isArray(workOrders) ? workOrders.filter(wo => wo.assignedUserId === currentUserId) : [];
          
          // Órdenes programadas para hoy -- fecha LOCAL, no UTC (ver comentario arriba).
          const today = new Date().toLocaleDateString('en-CA');
          const todayUserOrders = userOrders.filter(wo =>
            wo.scheduledDate && wo.scheduledDate.startsWith(today)
          );
          if (todayUserOrders.length > 0) {
            generatedNotifications.push({
              id: 'user-today-orders',
              type: 'info',
              message: `Tienes ${todayUserOrders.length} órdenes de trabajo programadas para hoy`,
              time: 'Ahora',
              action: () => navigate('/servicios/agenda'),
              actionLabel: 'Ver agenda'
            });
          }
          
          // Órdenes pendientes asignadas
          const pendingUserOrders = userOrders.filter(wo => wo.status === 'PENDIENTE' || wo.status === 'PROGRAMADA');
          if (pendingUserOrders.length > 0) {
            generatedNotifications.push({
              id: 'user-pending-orders',
              type: 'warning',
              message: `Tienes ${pendingUserOrders.length} órdenes de trabajo asignadas`,
              time: 'Ahora',
              action: () => navigate('/servicios/agenda'),
              actionLabel: 'Ver órdenes'
            });
          }
        }
        
        // Cotizaciones del usuario
        const userQuotations = quotations.filter(q => 
          q.userName === currentUser.name || q.createdBy === currentUser.name
        );
        
        const acceptedQuotations = userQuotations.filter(q => q.status === 'ACCEPTED');
        if (acceptedQuotations.length > 0) {
          generatedNotifications.push({
            id: 'accepted-quotations',
            type: 'success',
            message: `${acceptedQuotations.length} de tus cotizaciones han sido aceptadas`,
            time: 'Reciente',
            action: () => navigate('/estadisticas'),
            actionLabel: 'Ver detalles'
          });
        }
      }

      generatedNotifications.unshift(...buildQuotationReminders(quotations));

      // Si no hay notificaciones, mostrar mensaje positivo
      if (generatedNotifications.length === 0) {
        generatedNotifications.push({
          id: 'all-good',
          type: 'success',
          message: 'Todo está al día. ¡Excelente trabajo!',
          time: 'Ahora',
          action: null,
          actionLabel: null
        });
      }

      setNotifications(generatedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Fallback notifications
      setNotifications([
        {
          id: 'error',
          type: 'error',
          message: 'Error al cargar notificaciones',
          time: 'Ahora',
          action: () => window.location.reload(),
          actionLabel: 'Recargar'
        }
      ]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - eventDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hora${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''}`;
    return `${Math.floor(diffInMinutes / 1440)} día${Math.floor(diffInMinutes / 1440) > 1 ? 's' : ''}`;
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'QUOTATION_CREATED':
      case 'QUOTATION_ACCEPTED':
      case 'QUOTATION_REJECTED':
        return AssignmentIcon;
      case 'USER_CREATED':
      case 'USER_MODIFIED':
        return PeopleIcon;
      case 'SERVICE_CREATED':
      case 'SERVICE_MODIFIED':
        return BusinessIcon;
      default:
        return SettingsIcon;
    }
  };

  // revenueDetails ahora viene de revenueData.monthlyData (datos reales del backend)

  const getNotificationTypeFromBackend = (backendType) => {
    switch (backendType) {
      case 'WORK_ORDER_ASSIGNED':
      case 'WORK_ORDER_SCHEDULED':
        return 'info';
      case 'WORK_ORDER_COMPLETED':
        return 'success';
      case 'QUOTATION_CREATED':
        return 'info';
      case 'DOCUMENT_QA_APPROVED':
        return 'success';
      case 'DOCUMENT_QA_REJECTED':
        return 'error';
      case 'URGENT':
        return 'error';
      case 'HIGH':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Marca UNA notificación como leída en el backend (no la borra, solo isRead=true) y la quita
  // de esta lista. Compartido por "click en la fila/botón de acción" (que además navega) y por
  // el botón "×" de descartar (que NO navega -- solo marca esta, dejando las demás pendientes).
  const markNotificationReadOnly = async (notification) => {
    if (typeof notification.id === 'number' && currentUserId) {
      try {
        await api.put(`/notifications/${notification.id}/read?userId=${currentUserId}`);
        // Avisa a la campanita del Navbar para que baje el contador de inmediato -- sin esto
        // tardaba hasta 60s (su siguiente poll) o necesitaba F5 para reflejar lo que ya se quitó
        // aquí en la card.
        window.dispatchEvent(new Event('conem:notifications-updated'));
      } catch (e) {
        console.error('Error marcando notificación como leída:', e);
      }
    }
    if (typeof notification.id === 'string') dismissedReminders.current.add(notification.id);
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
  };

  const handleNotificationClick = async (notification) => {
    await markNotificationReadOnly(notification);
    if (notification.actionUrl) {
      navigate(notification.actionUrl, notification.actionState ? { state: notification.actionState } : undefined);
    } else if (typeof notification.action === 'function') {
      notification.action();
    }
  };

  // Descartar SOLO esta notificación sin salir de la pantalla -- antes la única forma de
  // marcar una como leída era hacer click en la fila o en su botón de acción, y eso además
  // navegaba a actionUrl; no había forma de solo "quitarla de la lista" sin irse de Home.
  const handleDismissNotification = (e, notification) => {
    e.stopPropagation();
    markNotificationReadOnly(notification);
  };

  const handleMarkAllNotificationsRead = async () => {
    if (!currentUserId) return;
    try {
      await api.put(`/notifications/read-all?userId=${currentUserId}`);
      setNotifications([]);
      window.dispatchEvent(new Event('conem:notifications-updated'));
    } catch (e) {
      console.error('Error marcando todas las notificaciones como leídas:', e);
    }
  };

  // Igual que arriba, pero para "Actividad Reciente" (tabla events). El estado de
  // leído es por (evento, usuario) en el backend, así que no afecta a otros usuarios
  // que también puedan ver el mismo evento.
  const handleActivityClick = async (activity) => {
    if (typeof activity.id === 'number') {
      try {
        await api.put(`/events/${activity.id}/read`);
      } catch (e) {
        console.error('Error marcando evento como leído:', e);
      }
    }
    setRecentActivity(prev => prev.filter(a => a.id !== activity.id));
  };

  const handleMarkAllActivityRead = async () => {
    try {
      await api.put('/events/read-all');
      setRecentActivity([]);
    } catch (e) {
      console.error('Error marcando toda la actividad como leída:', e);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning': return <WarningIcon sx={{ color: '#ff9800' }} />;
      case 'error': return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'success': return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      default: return <NotificationsIcon sx={{ color: '#2196f3' }} />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Chip
        label={appVersion ? `v${appVersion}` : 'v…'}
        size="small"
        variant="outlined"
        sx={{
          position: 'fixed',
          right: 12,
          bottom: 12,
          zIndex: 2000,
          bgcolor: 'rgba(255,255,255,0.9)',
          fontWeight: 600
        }}
      />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Dropbox Warning Banner — solo administrador, único que puede resolverlo */}
        {currentUser.role === 'ADMINISTRADOR' && dropboxHealth && dropboxHealth.issue && (
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 2
            }}
          >
            <WarningIcon sx={{ color: '#e65100', fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e65100' }}>
                Problema con Dropbox
              </Typography>
              <Typography variant="body2" sx={{ color: '#6d4c00' }}>
                {dropboxHealth.issue}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              sx={{ borderColor: '#e65100', color: '#e65100', textTransform: 'none', '&:hover': { bgcolor: '#fff8e1', borderColor: '#e65100' } }}
              onClick={() => navigate('/utilerias/dropbox')}
            >
              Configurar
            </Button>
          </Paper>
        )}

        {/* Hero de bienvenida */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 8px 24px rgba(21,101,192,0.25)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', width: 56, height: 56, fontSize: '1.2rem', fontWeight: 700 }}>
              {currentUser.name.split(' ').map(n => n[0]).join('')}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#fff' }}>
                  Bienvenido, {currentUser.name}
                </Typography>
                <Chip
                  label={appVersion ? `v${appVersion}` : 'v…'}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    height: 22,
                    letterSpacing: 0.5,
                  }}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {currentUser.role} • {new Date().toLocaleDateString('es-MX', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => window.location.reload()}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.28)',
                transform: 'rotate(180deg)',
                transition: 'all 0.3s ease-in-out'
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* KPIs Section - Solo para Administradores */}
        {(currentUser.role === 'ADMIN' || currentUser.role === 'ADMINISTRADOR') && (
          <>
        <Grid container spacing={3}>
          {/* KPIs Section — mismos datos y acciones de siempre, con el tratamiento visual del portal */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              sx={{
                height: 168,
                cursor: 'pointer',
                borderTop: '3px solid #1976d2',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
              }}
              onClick={() => setQuotationDetailsOpen(true)}
            >
              <CardContent sx={{ py: 2.5, px: 2.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2, bgcolor: '#e3f2fd',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5
                }}>
                  <AssignmentIcon sx={{ fontSize: 22, color: '#1976d2' }} />
                </Box>
                {quotationData.loading ? (
                  <CircularProgress size={22} sx={{ my: 0.5 }} />
                ) : (
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {quotationData.total}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  Cotizaciones totales
                </Typography>
                <Chip
                  label="Ver detalles"
                  size="small"
                  sx={{ mt: 1, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: '0.68rem' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              sx={{
                height: 168,
                cursor: 'pointer',
                borderTop: '3px solid #7b1fa2',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
              }}
              onClick={() => navigate('/usuarios/analitica')}
            >
              <CardContent sx={{ py: 2.5, px: 2.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2, bgcolor: '#f3e5f5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5
                }}>
                  <PeopleIcon sx={{ fontSize: 22, color: '#7b1fa2' }} />
                </Box>
                {userStats.loading ? (
                  <CircularProgress size={22} sx={{ my: 0.5 }} />
                ) : (
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {userStats.active}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  Usuarios activos
                </Typography>
                <Chip
                  label="Ver analítica"
                  size="small"
                  sx={{ mt: 1, bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '0.68rem' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              sx={{
                height: 168,
                cursor: 'pointer',
                borderTop: '3px solid #2e7d32',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
              }}
              onClick={() => setRevenueDetailsOpen(true)}
            >
              <CardContent sx={{ py: 2.5, px: 2.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2, bgcolor: '#e8f5e8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5
                }}>
                  <TrendingUpIcon sx={{ fontSize: 22, color: '#2e7d32' }} />
                </Box>
                {loadingRevenue ? (
                  <CircularProgress size={22} sx={{ my: 0.5 }} />
                ) : (
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {formatCompactCurrency(kpiData.revenue.projected)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  Ingresos proyectados
                </Typography>
                <Chip
                  label="Ver tendencias"
                  size="small"
                  sx={{ mt: 1, bgcolor: '#e8f5e8', color: '#2e7d32', fontWeight: 700, fontSize: '0.68rem' }}
                />
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <Card
              sx={{
                height: 168,
                cursor: 'pointer',
                borderTop: '3px solid #388e3c',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
              }}
              onClick={() => navigate('/estadisticas')}
            >
              <CardContent sx={{ py: 2.5, px: 2.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2, bgcolor: '#e8f5e8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5
                }}>
                  <AssessmentIcon sx={{ fontSize: 22, color: '#388e3c' }} />
                </Box>
                <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                  {quotationData.total}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  Estadísticas y métricas
                </Typography>
                <Chip
                  label="Ver análisis"
                  size="small"
                  sx={{ mt: 1, bgcolor: '#e8f5e8', color: '#388e3c', fontWeight: 700, fontSize: '0.68rem' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <Card
              sx={{
                height: 168,
                cursor: 'pointer',
                borderTop: `3px solid ${conversionStats.conversionRate >= 50 ? '#4caf50' : '#ff9800'}`,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
              }}
              onClick={() => navigate('/estadisticas')}
            >
              <CardContent sx={{ py: 2.5, px: 2.5 }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: conversionStats.conversionRate >= 50 ? '#e8f5e9' : '#fff3e0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5
                }}>
                  <TrendingUpIcon sx={{ fontSize: 22, color: conversionStats.conversionRate >= 50 ? '#4caf50' : '#ff9800' }} />
                </Box>
                {conversionStats.loading ? (
                  <CircularProgress size={22} sx={{ my: 0.5 }} />
                ) : (
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {conversionStats.conversionRate.toFixed(1)}%
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  % Conversión ({conversionStats.aceptadas}/{conversionStats.enviadas})
                </Typography>
                <Chip
                  label="Últimos 30 días"
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: conversionStats.conversionRate >= 50 ? '#e8f5e9' : '#fff3e0',
                    color: conversionStats.conversionRate >= 50 ? '#4caf50' : '#ff9800',
                    fontWeight: 700, fontSize: '0.68rem'
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: 450 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Actividad Semanal
              </Typography>
              {loadingWeeklyActivity ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={weeklyActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="cotizaciones" fill="#8884d8" name="Cotizaciones" />
                    <Bar dataKey="usuarios" fill="#82ca9d" name="Usuarios Activos" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: 450 }}>
              {/* Header con Tabs para cambiar vista */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Distribución de Cotizaciones
                </Typography>
                <Tabs 
                  value={distributionView} 
                  onChange={(e, newValue) => setDistributionView(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ minHeight: 40 }}
                >
                  <Tab 
                    label="Nivel de Riesgo" 
                    value="risk_level" 
                    sx={{ minHeight: 40, py: 1, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    label="Tipo de Cliente" 
                    value="client_type" 
                    sx={{ minHeight: 40, py: 1, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    label="Tipo de Servicio" 
                    value="service_type" 
                    sx={{ minHeight: 40, py: 1, fontSize: '0.875rem' }}
                  />
                  <Tab 
                    label="Grupo de Atención" 
                    value="attention_group" 
                    sx={{ minHeight: 40, py: 1, fontSize: '0.875rem' }}
                  />
                </Tabs>
              </Box>

              {/* Contenido del widget */}
              {loadingDistribution ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Fade in={!loadingDistribution} timeout={500}>
                  <Grid container spacing={2}>
                    {/* Gráfica de pastel */}
                    <Grid size={{ xs: 12, md: 7 }}>
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={serviceDistribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percentage }) => `${percentage}%`}
                            labelLine={false}
                          >
                            {serviceDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name, props) => [
                              `${value} cotizaciones (${props.payload.percentage}%)`,
                              props.payload.name
                            ]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid>

                    {/* Cards con estadísticas */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', justifyContent: 'center' }}>
                        {serviceDistribution.slice(0, 4).map((item, index) => (
                          <Card 
                            key={index}
                            sx={{ 
                              background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}05 100%)`,
                              border: `1px solid ${item.color}40`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateX(5px)',
                                boxShadow: `0 4px 12px ${item.color}30`
                              }
                            }}
                          >
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      bgcolor: item.color 
                                    }} 
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.name}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: item.color }}>
                                    {item.value}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.percentage}%
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                        {serviceDistribution.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center">
                            No hay datos disponibles
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Fade>
              )}
            </Paper>
          </Grid>
        </Grid>
          </>
        )}

        {currentUser.role === 'INVITADO' && <AvisoEntregasPendientes />}
        {/* Sección exclusiva para INVITADO */}
        {currentUser.role === 'INVITADO' && (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Mis Cotizaciones */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: 320 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Mis Cotizaciones</Typography>
                  <AssignmentIcon sx={{ color: '#1976d2' }} />
                </Box>
                {loadingGuestData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {guestQuotations.length} cotización{guestQuotations.length !== 1 ? 'es' : ''} en total
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { label: 'Pendientes', statuses: ['PENDING', 'DRAFT'], color: '#ff9800', bg: '#fff3e0' },
                        { label: 'Aceptadas', statuses: ['ACCEPTED'], color: '#4caf50', bg: '#e8f5e9' },
                        { label: 'Completadas', statuses: ['COMPLETED', 'INVOICED', 'PAID'], color: '#009688', bg: '#e0f2f1' },
                        { label: 'Rechazadas', statuses: ['REJECTED', 'CANCELLED', 'EXPIRED'], color: '#f44336', bg: '#ffebee' },
                      ].map(group => {
                        const count = guestQuotations.filter(q => group.statuses.includes(q.status)).length;
                        return (
                          <Grid size={{ xs: 6 }} key={group.label}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: group.bg, textAlign: 'center' }}>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: group.color }}>{count}</Typography>
                              <Typography variant="caption" sx={{ color: group.color }}>{group.label}</Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/perfil-riesgo/historial')}
                      sx={{ mt: 1 }}
                    >
                      Ver historial completo
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>

            {/* Próximas Inspecciones */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: 320 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Próximas Inspecciones</Typography>
                  <CalendarTodayIcon sx={{ color: '#1976d2' }} />
                </Box>
                {loadingGuestData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : guestWorkOrders.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: 'text.secondary' }}>
                    <CalendarTodayIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                    <Typography variant="body2">No hay inspecciones programadas</Typography>
                  </Box>
                ) : (
                  <List sx={{ maxHeight: 230, overflow: 'auto', p: 0 }}>
                    {guestWorkOrders.slice(0, 5).map((insp, idx) => {
                      const isPending   = insp.status === 'PENDIENTE';
                      const isInProgress = insp.status === 'EN_PROGRESO';
                      const chipConfig = isPending
                        ? { label: 'Por programar', bgcolor: '#fff3e0', color: '#e65100' }
                        : isInProgress
                          ? { label: 'En proceso',   bgcolor: '#e3f2fd', color: '#1976d2' }
                          : { label: 'Programada',   bgcolor: '#e8f5e9', color: '#388e3c' };
                      const iconColor = isPending ? '#ff9800' : isInProgress ? '#2196f3' : '#4caf50';
                      const dateStr = insp.scheduledDate
                        ? new Date(insp.scheduledDate + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'Fecha por definir';
                      const timeStr = insp.scheduledTimeStart ? ` · ${insp.scheduledTimeStart}` : '';
                      const location = insp.entityName || (insp.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal');
                      return (
                        <React.Fragment key={insp.id}>
                          <ListItem sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ScheduleIcon sx={{ color: iconColor, fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${insp.serviceType || 'Inspección'} — ${location}`}
                              secondary={`${dateStr}${timeStr}${insp.orderNumber ? ` · ${insp.orderNumber}` : ''}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            <Chip
                              label={chipConfig.label}
                              size="small"
                              sx={{ fontSize: '0.65rem', bgcolor: chipConfig.bgcolor, color: chipConfig.color }}
                            />
                          </ListItem>
                          {idx < guestWorkOrders.slice(0, 5).length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Accesos Rápidos */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: 320 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Accesos Rápidos</Typography>
                  <ArrowForwardIcon sx={{ color: '#1976d2' }} />
                </Box>
                <List sx={{ p: 0 }}>
                  {[
                    { label: 'Llenar información de mi empresa', icon: DescriptionIcon, route: '/perfil-riesgo/llenar-informacion' },
                    { label: 'Subir documentos faltantes', icon: CloudUploadIcon, route: '/perfil-riesgo/subir-archivos' },
                    { label: 'Ver historial de cotizaciones', icon: HistoryIcon, route: '/perfil-riesgo/historial' },
                    { label: 'Actualizar datos de mi empresa', icon: BusinessIcon, route: '/profile' },
                  ].map((action, idx, arr) => (
                    <React.Fragment key={action.route}>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => navigate(action.route)} sx={{ px: 1, borderRadius: 2 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <action.icon sx={{ color: '#1976d2', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={action.label} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                          <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </ListItemButton>
                      </ListItem>
                      {idx < arr.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Notificaciones, Actividad Reciente y Accesos Rápidos — panel de utilidad de 3 columnas */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, height: 350 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notificaciones
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {notifTab === 'mine' && notifications.length > 0 && (
                    <Button size="small" onClick={handleMarkAllNotificationsRead} sx={{ fontSize: '0.7rem', minWidth: 0, py: 0.3 }}>
                      Marcar todas
                    </Button>
                  )}
                  <Badge badgeContent={notifications.length} color="error">
                    <NotificationsIcon />
                  </Badge>
                </Box>
              </Box>
              {['ADMIN', 'ADMINISTRADOR'].includes(currentUser.role) && (
                <Tabs
                  value={notifTab}
                  onChange={(e, value) => { setNotifTab(value); if (value === 'supervision') fetchSupervisionFeed(); }}
                  variant="fullWidth"
                  sx={{ minHeight: 32, mt: -1, mb: 1, '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                >
                  <Tab value="mine" label="Mis notificaciones" />
                  <Tab value="supervision" label="Supervisión" />
                </Tabs>
              )}
              {notifTab === 'supervision' ? (
                loadingSupervision ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 210 }}>
                    <CircularProgress />
                  </Box>
                ) : supervisionFeed.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
                    Sin notificaciones en los últimos 30 días
                  </Typography>
                ) : (
                  <List sx={{ maxHeight: 210, overflow: 'auto' }}>
                    {supervisionFeed.map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, alignItems: 'flex-start', display: 'block', borderBottom: '1px solid #f0f0f0', pb: 1 }}>
                        <Typography variant="body2">{item.message}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.timeAgo || ''}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {(item.recipients || []).map(r => (
                            <Chip
                              key={r.userId}
                              size="small"
                              variant="outlined"
                              label={`${r.isRead ? '✓' : '●'} ${r.name}`}
                              title={`${r.role || ''} · ${r.isRead ? 'leída' : 'sin leer'}`}
                              sx={{ height: 20, fontSize: '0.68rem', color: r.isRead ? '#2e7d32' : '#ed6c02', borderColor: r.isRead ? '#a5d6a7' : '#ffcc80' }}
                            />
                          ))}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )
              ) : loadingNotifications ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List sx={{ maxHeight: ['ADMIN', 'ADMINISTRADOR'].includes(currentUser.role) ? 210 : 250, overflow: 'auto' }}>
                  {notifications.map((notification) => (
                    <ListItem
                      key={notification.id}
                      sx={{
                        px: 0,
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <ListItemIcon sx={{ mt: 0.5 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.message}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              {notification.time}
                            </Typography>
                            {notification.actionLabel && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{ fontSize: '0.72rem', py: 0.3 }}
                                onClick={(e) => { e.stopPropagation(); handleNotificationClick(notification); }}
                              >
                                📅 {notification.actionLabel}
                              </Button>
                            )}
                          </Box>
                        }
                      />
                      {/* Descarta SOLO esta notificación (marca como leída sin navegar) -- las
                          demás se quedan pendientes. Antes la única forma de marcar como leída
                          era hacer click en la fila/botón de acción, que también navegaba. */}
                      <IconButton
                        size="small"
                        aria-label="Marcar como leída"
                        onClick={(e) => handleDismissNotification(e, notification)}
                        sx={{ ml: 0.5, mt: 0.5, color: 'text.disabled', '&:hover': { color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.06)' } }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, height: 350 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Actividad Reciente
                </Typography>
                {recentActivity.length > 0 && (
                  <Button size="small" onClick={handleMarkAllActivityRead} sx={{ fontSize: '0.7rem', minWidth: 0, py: 0.3 }}>
                    Marcar todas
                  </Button>
                )}
              </Box>
              {loadingActivity ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                  <CircularProgress />
                </Box>
              ) : recentActivity.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 250, color: '#999' }}>
                  <AssessmentIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1" color="textSecondary">
                    No hay actividad reciente
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                  {recentActivity.map((activity, index) => {
                    let parsedMeta = null;
                    try { parsedMeta = activity.metadata ? JSON.parse(activity.metadata) : null; } catch {}
                    const isAccepted = parsedMeta?.newStatus === 'ACCEPTED';
                    return (
                      <React.Fragment key={activity.id}>
                        <ListItem
                          sx={{ px: 0, alignItems: 'flex-start', cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                          onClick={() => handleActivityClick(activity)}
                        >
                          <ListItemIcon sx={{ mt: 0.5 }}>
                            <activity.icon sx={{ color: '#1976d2' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={activity.action}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <span>{activity.user} • Hace {activity.time}</span>
                                {isAccepted && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 0.5, alignSelf: 'flex-start', fontSize: '0.72rem', py: 0.3 }}
                                    onClick={(e) => { e.stopPropagation(); navigate('/servicios/agenda'); }}
                                  >
                                    📅 Ver Agenda de Servicios
                                  </Button>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentActivity.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Accesos Rápidos — usuarios staff (no invitado) */}
          {currentUser.role && currentUser.role !== 'INVITADO' && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: 350 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Accesos Rápidos</Typography>
                  <ArrowForwardIcon sx={{ color: '#1976d2' }} />
                </Box>
                <List sx={{ p: 0 }}>
                  {/* Antes esta lista era fija para todo el personal, incluyendo rutas de
                      Servicios (Agenda/Programas/Gestión Documental) a las que Ventas NO tiene
                      permiso -- validado contra role_screens: Ventas solo puede entrar a
                      Prospectos, Cotizaciones (Nueva/Gestión), Sucursales, Abonos y Capacitaciones.
                      Se le muestran los 2 menús que más usa: Prospectos y Cotizaciones. */}
                  {(currentUser.role === 'VENTAS' ? [
                    { label: 'Prospectos', icon: BusinessIcon, route: '/clientes/empresas' },
                    { label: 'Nueva Cotización', icon: AddCircleIcon, route: '/cotizaciones/new-quotation' },
                    { label: 'Cotizaciones / Gestión', icon: AssessmentIcon, route: '/estadisticas' },
                  ] : [
                    { label: 'Agenda de Servicios', icon: CalendarTodayIcon, route: '/servicios/agenda' },
                    { label: 'Programas', icon: AssignmentIcon, route: '/servicios/programas' },
                    { label: 'Gestión Documental', icon: FolderIcon, route: '/servicios/documentos' },
                    { label: 'Cotizaciones / Gestión', icon: AssessmentIcon, route: '/estadisticas' },
                  ]).map((action, idx, arr) => (
                    <React.Fragment key={action.route}>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => navigate(action.route)} sx={{ px: 1, borderRadius: 2 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <action.icon sx={{ color: '#1976d2', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={action.label} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                          <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </ListItemButton>
                      </ListItem>
                      {idx < arr.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Quotation Details Dialog */}
        <Dialog 
          open={quotationDetailsOpen} 
          onClose={() => setQuotationDetailsOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <AssignmentIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Detalle de Cotizaciones
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 3, px: 3, bgcolor: '#fafbfc' }}>
            {quotationData.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Cards de Resumen */}
                <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
                  {[
                    { label: 'Total', value: quotationData.total, color: '#1976d2', bgcolor: '#e3f2fd', Icon: AssignmentIcon },
                    { label: 'Pendientes', value: quotationData.pending, color: '#f57c00', bgcolor: '#fff3e0', Icon: ScheduleIcon },
                    { label: 'Aprobadas', value: quotationData.approved, color: '#388e3c', bgcolor: '#e8f5e9', Icon: CheckCircleIcon },
                    { label: 'Rechazadas', value: quotationData.rejected, color: '#d32f2f', bgcolor: '#ffebee', Icon: CancelIcon },
                  ].map(({ label, value, color, bgcolor, Icon }) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={label}>
                      <Card sx={{
                        bgcolor,
                        borderLeft: `4px solid ${color}`,
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' }
                      }}>
                        <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                          <Icon sx={{ fontSize: 36, color, mb: 0.5 }} />
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                        Distribución por Estado
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={[
                              { status: 'Pendientes', count: quotationData.pending, color: '#ff9800' },
                              { status: 'Aprobadas', count: quotationData.approved, color: '#4caf50' },
                              { status: 'Rechazadas', count: quotationData.rejected, color: '#f44336' }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            fill="#8884d8"
                            dataKey="count"
                            label={({ count, percent }) => count > 0 ? `${(percent * 100).toFixed(0)}%` : ''}
                            labelLine={false}
                          >
                            {[
                              { status: 'Pendientes', count: quotationData.pending, color: '#ff9800' },
                              { status: 'Aprobadas', count: quotationData.approved, color: '#4caf50' },
                              { status: 'Rechazadas', count: quotationData.rejected, color: '#f44336' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [value, props.payload.status]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HistoryIcon sx={{ fontSize: 20 }} />
                        Cotizaciones Recientes
                      </Typography>
                      {quotationData.recentQuotations.length > 0 ? (
                        <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Folio</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Cliente</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Monto</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Fecha</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {quotationData.recentQuotations.map((quote) => (
                                <TableRow key={quote.id} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                                  <TableCell sx={{ color: '#1976d2', fontWeight: 600, fontSize: '0.8rem' }}>{quote.id}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>{quote.client}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>${quote.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                  <TableCell>
                                    <Chip label={quote.status} color={quote.statusColor} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{quote.date}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <AssignmentIcon sx={{ fontSize: 64, color: '#bdbdbd', opacity: 0.5 }} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            No hay cotizaciones recientes
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f5f5f5', gap: 1 }}>
            <Button 
              onClick={() => navigate('/estadisticas')} 
              variant="outlined"
              startIcon={<AssessmentIcon />}
              sx={{ borderRadius: 2 }}
            >
              Ver Estadísticas
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button 
              onClick={() => navigate('/cotizaciones/new-quotation')} 
              variant="contained"
              startIcon={<AddCircleIcon />}
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                }
              }}
            >
              Nueva Cotización
            </Button>
            <Button 
              onClick={() => setQuotationDetailsOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Revenue Details Dialog */}
        <Dialog open={revenueDetailsOpen} onClose={() => setRevenueDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Tendencias de Ingresos - Últimos 6 Meses
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ height: 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatCompactCurrency(value)} />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString('es-MX')}`, '']} />
                  <Area type="monotone" dataKey="ingresos" stackId="1" stroke="#8884d8" fill="#8884d8" name="Ingresos Reales" />
                  <Area type="monotone" dataKey="proyectado" stackId="2" stroke="#82ca9d" fill="#82ca9d" name="Proyectado" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                    <Typography variant="h6" color="primary">
                      {formatCompactCurrency(revenueData.totalProjected)}
                    </Typography>
                    <Typography variant="body2">
                      Total Proyectado (6 meses)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e8' }}>
                    <Typography variant="h6" color="success.main">
                      {formatCompactCurrency(revenueData.totalReal)}
                    </Typography>
                    <Typography variant="body2">
                      Total Real (6 meses)
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                    <Typography variant="h6" color="warning.main">
                      {revenueData.totalProjected > 0 
                        ? ((revenueData.totalReal / revenueData.totalProjected) * 100).toFixed(1) 
                        : 0}%
                    </Typography>
                    <Typography variant="body2">
                      Tasa de Conversión
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => navigate('/estadisticas')} variant="contained">
              Ver Estadísticas Completas
            </Button>
            <Button onClick={() => setRevenueDetailsOpen(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Home;
